import { Children, type ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import { CalendarDays, Clock, RefreshCw, Sparkles, ListTree, User } from "lucide-react";
import { ShareButtons } from "./ShareButtons";
import { headingSlug, stripInlineMd, type OutlineItem } from "@/lib/blog/article-outline";

/** Renders children back to a plain string so headings can get stable ids. */
function nodeText(node: ReactNode): string {
  let out = "";
  Children.forEach(node, (child) => {
    if (typeof child === "string" || typeof child === "number") out += String(child);
    else if (child && typeof child === "object" && "props" in child) {
      out += nodeText((child as { props: { children?: ReactNode } }).props.children);
    }
  });
  return out;
}

/**
 * Markdown heading renderers that add scroll anchors matching the outline ids.
 */
export function headingComponents() {
  const make = (level: 2 | 3) =>
    function Heading({ children }: { children?: ReactNode }) {
      const text = stripInlineMd(nodeText(children));
      const id = headingSlug(text);
      return level === 2 ? (
        <h2 id={id} className="scroll-mt-28">
          {children}
        </h2>
      ) : (
        <h3 id={id} className="scroll-mt-28">
          {children}
        </h3>
      );
    };
  // Tables scroll inside their own box so wide comparisons never push the
  // page sideways on phones.
  function Table({ children }: { children?: ReactNode }) {
    return (
      <div className="my-8 w-full overflow-x-auto rounded-lg border border-border">
        <table className="w-full min-w-[32rem] border-collapse text-left text-sm [&_td]:border-t [&_td]:border-border [&_td]:px-4 [&_td]:py-3 [&_td]:align-top [&_th]:bg-muted [&_th]:px-4 [&_th]:py-3 [&_th]:font-semibold [&_th]:text-brand-dark">
          {children}
        </table>
      </div>
    );
  }
  return { h2: make(2), h3: make(3), table: Table };
}

export interface ArticleHeaderProps {
  category: string;
  categoryTo?: { to: string; params?: Record<string, string> };
  title: string;
  excerpt?: string | null;
  author: string;
  published: string;
  publishedDateTime?: string | null;
  updated?: string | null;
  updatedDateTime?: string | null;
  readMins?: number | null;
  path: string;
}

/** Branded article masthead: breadcrumb, category chip, title, byline, sharing. */
export function ArticleHeader({
  category,
  title,
  excerpt,
  author,
  published,
  publishedDateTime,
  updated,
  updatedDateTime,
  readMins,
  path,
}: ArticleHeaderProps) {
  return (
    <header className="relative overflow-hidden border-b-4 border-brand-gold bg-brand-gradient text-brand-foreground">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 bottom-0 h-px bg-brand-gold/70"
      />
      <div className="container-editorial relative px-5 py-10 sm:px-7 md:px-10 md:py-14">
        <nav aria-label="Breadcrumb" className="text-xs text-brand-foreground/70">
          <Link to="/" className="hover:text-brand-gold">
            Home
          </Link>
          <span className="mx-2">›</span>
          <Link to="/blog" className="hover:text-brand-gold">
            Blog
          </Link>
          <span className="mx-2">›</span>
          <span className="text-brand-foreground/90">{category}</span>
        </nav>

        <span className="eyebrow mt-5 inline-block rounded-full border border-brand-gold/60 bg-brand-gold/15 px-3 py-1 text-brand-gold">
          {category}
        </span>

        <h1 className="mt-4 max-w-4xl break-words font-display text-3xl font-bold leading-tight text-brand-foreground md:text-5xl">
          {title}
        </h1>

        {excerpt && (
          <p className="mt-4 max-w-3xl text-base leading-relaxed text-brand-foreground/82 md:text-lg">
            {excerpt}
          </p>
        )}

        <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-brand-foreground/75">
          <span className="inline-flex items-center gap-1.5">
            <User className="h-4 w-4 text-brand-gold" aria-hidden /> {author}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <CalendarDays className="h-4 w-4 text-brand-gold" aria-hidden /> Published{" "}
            <time dateTime={publishedDateTime ?? undefined}>{published}</time>
          </span>
          {updated && updated !== published && (
            <span className="inline-flex items-center gap-1.5">
              <RefreshCw className="h-4 w-4 text-brand-gold" aria-hidden /> Updated{" "}
              <time dateTime={updatedDateTime ?? undefined}>{updated}</time>
            </span>
          )}
          {readMins ? (
            <span className="inline-flex items-center gap-1.5">
              <Clock className="h-4 w-4 text-brand-gold" aria-hidden /> {readMins} min read
            </span>
          ) : null}
        </div>

        <div className="mt-6">
          <ShareButtons path={path} title={title} variant="full" />
        </div>
      </div>
    </header>
  );
}

/** Answer-first quick answer + key takeaways block. */
export function ArticleTakeaways({
  answer,
  points,
}: {
  answer?: string;
  points: string[];
}) {
  if (!answer && points.length === 0) return null;
  return (
    <aside
      data-answer-first
      className="overflow-hidden rounded-lg border-l-4 border-brand-gold bg-brand p-6 text-brand-foreground shadow-lg md:p-8"
      aria-labelledby="article-quick-answer-heading"
    >
      <div
        id="article-quick-answer-heading"
        className="flex items-center gap-2 text-sm font-bold uppercase tracking-widest text-brand-gold"
      >
        <Sparkles className="h-4 w-4 text-accent" aria-hidden />
        Quick answer
      </div>
      <h2 className="mt-3 font-display text-2xl font-bold text-brand-foreground">TL;DR</h2>
      {answer && (
        <p id="answer-summary" data-speakable className="mt-2 text-lg font-semibold leading-relaxed text-brand-foreground">
          {answer}
        </p>
      )}
      {points.length > 0 && (
        <>
          <p className="mt-5 text-xs font-bold uppercase tracking-widest text-brand-gold">
            Key takeaways
          </p>
          <ul className="mt-2 space-y-2 text-sm leading-relaxed text-brand-foreground/90">
            {points.map((p, i) => (
              <li key={i} className="flex gap-2">
                <span className="mt-2 inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-brand-gold" />
                <span>{p}</span>
              </li>
            ))}
          </ul>
        </>
      )}
    </aside>
  );
}

/** In-article outline, rendered only when the piece is long enough to need it. */
export function ArticleOutline({ items }: { items: OutlineItem[] }) {
  const tops = items.filter((i) => i.level === 2);
  if (tops.length < 3) return null;
  return (
    <nav
      aria-labelledby="article-outline-heading"
      className="min-w-0 overflow-hidden rounded-xl border border-border bg-card p-5"
    >
      <p
        id="article-outline-heading"
        className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-brand-dark"
      >
        <ListTree className="h-4 w-4 text-accent" aria-hidden /> In this article
      </p>
      <ol className="mt-3 space-y-2 text-sm">
        {tops.map((i, idx) => (
          <li key={i.id} className="flex gap-2">
            <span className="text-muted-foreground">{idx + 1}.</span>
            <a href={`#${i.id}`} className="min-w-0 break-words text-foreground/90 hover:text-primary hover:underline">
              {i.text}
            </a>
          </li>
        ))}
      </ol>
    </nav>
  );
}

/** End-of-article share strip. */
export function ArticleShareFooter({ path, title }: { path: string; title: string }) {
  return (
    <div className="flex flex-col gap-3 border-t border-border pt-5 sm:flex-row sm:items-center sm:justify-between">
      <p className="text-sm font-semibold text-foreground">Share this article</p>
      <ShareButtons path={path} title={title} />
    </div>
  );
}
